import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Bell, Loader2, Save, Mail, Calendar, DollarSign, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { DEFAULT_NOTIFICATION_SETTINGS, type NotificationSettings } from '@/lib/settings-service';

interface NotificationOption {
  key: keyof NotificationSettings;
  label: string;
  description: string;
  icon: typeof Bell;
}

const NOTIFICATION_OPTIONS: NotificationOption[] = [
  {
    key: 'emailNewBookings',
    label: 'New Bookings',
    description: 'Receive email when a new consultation is booked',
    icon: Calendar,
  },
  {
    key: 'emailPaymentReceived',
    label: 'Payment Received',
    description: 'Receive email when a payment is completed',
    icon: DollarSign,
  },
  {
    key: 'emailAppointmentReminders',
    label: 'Appointment Reminders',
    description: 'Send reminder emails before appointments',
    icon: Bell,
  },
  {
    key: 'emailDailyDigest',
    label: 'Daily Digest',
    description: 'Receive a daily summary of activity',
    icon: FileText,
  },
];

export default function NotificationsSection() {
  const [settings, setSettings] = useState<NotificationSettings>(DEFAULT_NOTIFICATION_SETTINGS);
  const [loading, setLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const handleToggle = (key: keyof NotificationSettings) => {
    setSettings(prev => ({ ...prev, [key]: !prev[key] }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setLoading(true);
    // In a full implementation, this would save to a settings table in the database
    await new Promise(resolve => setTimeout(resolve, 500));
    toast.success('Notification preferences saved successfully');
    setHasChanges(false);
    setLoading(false);
  };

  const handleReset = () => {
    setSettings(DEFAULT_NOTIFICATION_SETTINGS);
    setHasChanges(false);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="h-5 w-5" />
          Notifications
        </CardTitle>
        <CardDescription>
          Configure email notifications and alerts
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          <div className="flex items-center gap-3 p-4 rounded-lg bg-muted/50 border">
            <Mail className="h-8 w-8 text-muted-foreground" />
            <div>
              <p className="font-medium">Email Notifications</p>
              <p className="text-sm text-muted-foreground">
                Notifications are sent to the team email configured in your integrations
              </p>
            </div>
          </div>

          <div className="space-y-4">
            {NOTIFICATION_OPTIONS.map((option) => {
              const Icon = option.icon;
              
              return (
                <div
                  key={option.key}
                  className="flex items-center justify-between p-4 rounded-lg border"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-primary/10">
                      <Icon className="h-4 w-4 text-primary" />
                    </div>
                    <div>
                      <Label htmlFor={option.key} className="font-medium cursor-pointer">
                        {option.label}
                      </Label>
                      <p className="text-sm text-muted-foreground">{option.description}</p>
                    </div>
                  </div>
                  <Switch
                    id={option.key}
                    checked={settings[option.key]}
                    onCheckedChange={() => handleToggle(option.key)}
                  />
                </div>
              );
            })}
          </div>

          <div className="flex gap-2 pt-4 border-t">
            <Button onClick={handleSave} disabled={loading || !hasChanges}>
              {loading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Save Changes
            </Button>
            {hasChanges && (
              <Button variant="outline" onClick={handleReset}>
                Reset
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
